import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { BlockType } from '@common/entities';
import { VerificationFieldState } from 'state/slices/idIncomeVerificationSlice';
import type { RootState } from 'state/store';

import { allowToCompleteTheApplication } from './verificationHelpers';

/**
 * Hook to validate ID and Income verification status and determine if user can move forward
 * @param pageIndex - The current page index to validate against
 * @returns Object containing canMoveForward flag and message
 */
export const useValidateIDIncomeVerification = () => {
  const { idVerification, incomeVerification } = useSelector(
    (state: RootState) => state.idIncomeVerification,
  );

  const { getValues } = useFormContext();
  const idVerificationStatus = getValues(`${BlockType.IDVerification}.status`);
  const incomeVerificationStatus = getValues(`${BlockType.IncomeVerification}.status`);

  /**
   * Check ID verification rules
   */
  const checkIdRules = (fieldVerification: VerificationFieldState): boolean => {
    return allowToCompleteTheApplication(fieldVerification);
  };

  /**
   * Check Income verification rules
   * Note: Payroll and Document verification types always allow forward navigation
   */
  const checkIncomeRules = (fieldVerification: VerificationFieldState): boolean => {
    return allowToCompleteTheApplication(fieldVerification);
  };

  return useMemo(() => {
    const isIdVerificationComplete =
      idVerificationStatus !== undefined ? checkIdRules(idVerification) : true;
    const isIncomeVerificationComplete =
      incomeVerificationStatus !== undefined ? checkIncomeRules(incomeVerification) : true;

    // Both ID and income verification rules must pass
    return isIdVerificationComplete && isIncomeVerificationComplete;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idVerification, incomeVerification]);
};
